import React from 'react';

const hasValue = (value) => value !== null && value !== undefined && value !== '';

function numberValue(value) {
  if (!hasValue(value)) return '';
  const n = Number(value);
  const raw = Number.isFinite(n) ? String(Math.trunc(n)) : String(value).split('.')[0];
  return raw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function parseNumber(value) {
  const integerPart = String(value || '').replace(/,/g, '').split('.')[0];
  const digits = integerPart.replace(/[^\d]/g, '');
  return digits === '' ? null : Number(digits);
}

const PAYMENT_FIELDS = [
  { key: 'payment_date', fieldKey: 'payment.payment_date', label: '지불일', type: 'date' },
  { key: 'amount', fieldKey: 'payment.amount', label: '고객지불', type: 'amount' },
  { key: 'payment_method', fieldKey: 'payment.payment_method', label: '지불수단', type: 'select', compact: true },
  { key: 'cash_receipt_issue_no', fieldKey: 'payment.cash_receipt_issue_no', label: '현금영수증번호' },
  { key: 'cash_receipt_amount', fieldKey: 'payment.cash_receipt_amount', label: '현금영수증금액', type: 'amount' },
  { key: 'card_deposit_memo', fieldKey: 'payment.card_deposit_memo', label: '카드입금 메모', wide: true },
];
const CASH_RECEIPT_KEYS = new Set(['cash_receipt_issue_no', 'cash_receipt_amount']);
const CARD_DEPOSIT_KEYS = new Set(['card_deposit_memo']);

export default function ReservationPaymentRows({
  rows = [],
  showField,
  methodOptions = [],
  onChange,
  onRemove,
}) {
  const visibleFields = PAYMENT_FIELDS.filter((field) => showField(field.fieldKey));
  if (!rows.length) return <div style={emptyStyle}>결제 내역 없음 - "+ 추가" 클릭</div>;

  return (
    <div style={listStyle}>
      {rows.map((row, rowIndex) => {
        const rowFields = visibleFields.filter((field) => (
          (!CASH_RECEIPT_KEYS.has(field.key) || row.payment_method === 'cash') &&
          (!CARD_DEPOSIT_KEYS.has(field.key) || row.payment_method === 'card')
        ));
        return (
          <div key={rowIndex} style={rowStyle}>
            <div style={rowHeaderStyle}>
              <strong style={rowTitleStyle}>결제 #{rowIndex + 1}</strong>
              <button type="button" onClick={() => onRemove(rowIndex)} style={deleteBtnStyle}>삭제</button>
            </div>
            <div style={fieldGridStyle}>
              {rowFields.map((field) => (
                <CompactField
                  key={field.key}
                  field={field}
                  value={row[field.key]}
                  methodOptions={methodOptions}
                  onChange={(value) => onChange(rowIndex, field.key, value)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CompactField({ field, value, methodOptions, onChange }) {
  return (
    <label className={`reservation-payment-field reservation-payment-field--${field.key}`} style={fieldWrapStyle(field)}>
      <span style={labelStyle}>{field.label}</span>
      <FieldControl field={field} value={value} methodOptions={methodOptions} onChange={onChange} />
    </label>
  );
}

function FieldControl({ field, value, methodOptions, onChange }) {
  if (field.type === 'select') {
    return (
      <select value={value ?? ''} onChange={(event) => onChange(event.target.value || null)} style={inputStyle}>
        <option value="">선택</option>
        {methodOptions.map((option) => (
          <option key={option.value ?? option} value={option.value ?? option}>{option.label ?? option}</option>
        ))}
      </select>
    );
  }

  if (field.type === 'amount') {
    return (
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9,]*"
        value={numberValue(value)}
        onFocus={(event) => event.target.select()}
        onChange={(event) => onChange(parseNumber(event.target.value))}
        style={amountInputStyle}
      />
    );
  }

  return (
    <input
      type={field.type || 'text'}
      value={value ?? ''}
      onChange={(event) => onChange(event.target.value || null)}
      style={inputStyle}
    />
  );
}

const listStyle = { display: 'grid', gap: 6, minWidth: 0 };
const rowStyle = {
  display: 'grid',
  gridTemplateColumns: '86px minmax(0, 1fr)',
  gap: 7,
  alignItems: 'end',
  padding: 7,
  border: '1px solid var(--border)',
  borderRadius: 7,
  background: 'var(--bg)',
  minWidth: 0,
};
const rowHeaderStyle = { display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'space-between', minWidth: 0 };
const rowTitleStyle = { fontSize: 12, color: 'var(--text)', whiteSpace: 'nowrap' };
const fieldGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))', gap: 6, minWidth: 0 };
const fieldWrapStyle = (field) => ({
  display: 'grid',
  gap: 2,
  minWidth: field.compact ? 90 : 0,
  gridColumn: field.wide ? 'span 2' : undefined,
});
const labelStyle = { fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.1, whiteSpace: 'nowrap' };
const inputStyle = {
  width: '100%',
  height: 30,
  padding: '5px 8px',
  border: '1px solid var(--border)',
  borderRadius: 4,
  background: 'var(--bg-card)',
  color: 'var(--text)',
  boxSizing: 'border-box',
  fontSize: 12,
};
const amountInputStyle = { ...inputStyle, textAlign: 'right' };
const deleteBtnStyle = {
  height: 26,
  padding: '2px 6px',
  background: 'transparent',
  color: '#dc2626',
  border: '1px solid #dc2626',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 10,
};
const emptyStyle = { padding: 8, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 };
